import { useState, useEffect } from "react";
import axios from "axios";
import { Box, TextField, Typography, Checkbox, FormControlLabel } from "@mui/material";
import { ApiGateway } from "../config/config";
import { loginWithHint } from "./auth";
import { Stack } from "@mui/system";
import { LoadingButton } from "@mui/lab";

const LoginEmailCapture = () => {
  const [email, setEmail] = useState(localStorage.getItem("rememberedEmail") || ""); // Load email from localStorage if available
  const [userP, setUserP] = useState(localStorage.getItem("rememberedUserP") || "")
  const [rememberMe, setRememberMe] = useState(localStorage.getItem("rememberMe") !== "false"); // Load toggle state from localStorage
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [validating, setValidating] = useState(false)

  const validateEmail = (email) => {
    // Basic email format validation using regular expression
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const input = e.target.value;
    setEmail(input);

    // Validate email on input change
    if (validateEmail(input)) {
      setErrorMessage(""); // Clear error if email is valid
    } else {
      setErrorMessage("Email address format is invalid.");
    }

    // Clear success message while typing
    setSuccessMessage("");

    // Save email to localStorage if "Remember Me" is enabled
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", input);
    }
  };

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);

    // Save toggle state and email to localStorage if enabled
    localStorage.setItem("rememberMe", checked);

    if (!checked) {
      // Clear email if "Remember Me" is disabled
      localStorage.removeItem("rememberedEmail");
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      setSuccessMessage(""); // Clear success message
      return;
    }
    setValidating(true)
    setErrorMessage(""); // Clear error message upon valid email

    try {
      const response = await axios.post(`${ApiGateway.domain}/user/hint`, {
        email: email,
      });

      if (response.status === 200) {
        setSuccessMessage("Email submitted successfully!");
        const hint = response.data?.up;
        if (hint) {
          localStorage.setItem("rememberedUserP", hint)
          loginWithHint(hint)
        }
      } else {
        setErrorMessage("Failed to submit email.");
      }
    } catch (error) {
      if (error.response?.data === 'No Access') {
        setErrorMessage('We could not find a registered account.')
      } else {
        setErrorMessage("An error occurred while submitting the email.")
      }
    } finally {
      setValidating(false)
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    // Automatically store email if Remember Me is enabled when the component mounts
    if (rememberMe && email) {
      localStorage.setItem("rememberedEmail", email);
    }
  }, [rememberMe, email]);

  return (
    <Box
      sx={{
        maxWidth: 500,
        margin: "auto",
        padding: 4,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h6" align="center" gutterBottom>
        Please enter your registered email address:
      </Typography>
      <Stack direction="row" columnGap={1}>
        <TextField
          size="small"
          type="email"
          placeholder="Your email"
          value={email}
          onChange={handleEmailChange}
          onKeyUp={handleKeyPress}
          error={Boolean(errorMessage)} // Show error state if validation fails
          helperText={errorMessage} // Show error message directly
          fullWidth
          variant="outlined"
        />
      </Stack>
      <Box justifyContent='space-between'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
            }
            label="Remember My Email"
          />
          <div>
          <LoadingButton
            type="submit"
            size="small"
            loading={validating}
            variant="contained"
            color={errorMessage ? "error" : "primary"} // Set button color based on validation
            onClick={handleSubmit}
            disabled={Boolean(errorMessage)} // Disable button if error exists
          >
            Submit
          </LoadingButton>
        </div>
        </Stack>
      </Box>
      {successMessage && (
        <Typography color="success" align="center" variant="body2" mt={2}>
          {successMessage}
        </Typography>
      )}
    </Box>
  );
};

export default LoginEmailCapture;